import { ReportViewModel } from 'types/report';

// Prompt versions are global and not tied to specific reports or URL params
const PROMPT_VERSION_CACHE_KEY = 'ai-editor-prompt-versions-global';

export interface PromptVersion {
  id: string;
  name: string;
  content: string;
  timestamp: number;
  isOriginal?: boolean;
}

export interface PromptVersionGroup {
  versions: PromptVersion[];
  activeVersionId: string;
  lastModified: number;
}

// Generate cache key for prompt versions - now global, not report-specific
const generatePromptVersionCacheKey = (_reportData?: ReportViewModel | null | undefined) => {
  // Prompt versions are global templates that work across all reports
  return PROMPT_VERSION_CACHE_KEY;
};

export class PromptVersionService {
  // Get all versions for a report
  static getVersionGroup(reportData: ReportViewModel | null | undefined): PromptVersionGroup | null {
    const cacheKey = generatePromptVersionCacheKey(reportData);
    if (!cacheKey) return null;

    try {
      const cachedGroup = localStorage.getItem(cacheKey);
      if (!cachedGroup) return null;

      return JSON.parse(cachedGroup) as PromptVersionGroup;
    } catch (error) {
      console.error('Failed to get prompt version group from cache:', error);
      return null;
    }
  }

  // Initialize version group with original prompt
  static initializeVersionGroup(
    reportData: ReportViewModel | null | undefined,
    originalPrompt: string
  ): PromptVersionGroup {
    const originalVersion: PromptVersion = {
      id: 'original',
      name: 'Original',
      content: originalPrompt,
      timestamp: Date.now(),
      isOriginal: true,
    };

    const group: PromptVersionGroup = {
      versions: [originalVersion],
      activeVersionId: 'original',
      lastModified: Date.now(),
    };

    this.saveVersionGroup(reportData, group);
    return group;
  }

  // Save version group to localStorage
  static saveVersionGroup(reportData: ReportViewModel | null | undefined, group: PromptVersionGroup): void {
    const cacheKey = generatePromptVersionCacheKey(reportData);
    if (!cacheKey) return;

    try {
      group.lastModified = Date.now();
      localStorage.setItem(cacheKey, JSON.stringify(group));
    } catch (error) {
      console.error('Failed to save prompt version group to cache:', error);
    }
  }

  // Create a new version
  static createNewVersion(
    reportData: ReportViewModel | null | undefined,
    content: string,
    basedOnVersionId?: string
  ): PromptVersionGroup | null {
    let group = this.getVersionGroup(reportData);

    if (!group) {
      // If no group exists, initialize with the content as original
      group = this.initializeVersionGroup(reportData, content);
      return group;
    }

    // Generate next version number
    const versionNumbers = group.versions
      .filter(v => v.name.startsWith('prompt-'))
      .map(v => parseInt(v.name.replace('prompt-', ''), 10))
      .filter(n => !isNaN(n));

    const nextNumber = versionNumbers.length > 0 ? Math.max(...versionNumbers) + 1 : 1;

    const newVersion: PromptVersion = {
      id: `version-${Date.now()}`,
      name: `prompt-${nextNumber}`,
      content,
      timestamp: Date.now(),
    };

    group.versions.push(newVersion);
    group.activeVersionId = newVersion.id;

    this.saveVersionGroup(reportData, group);
    return group;
  }

  // Save changes to existing version
  static saveVersion(
    reportData: ReportViewModel | null | undefined,
    versionId: string,
    content: string
  ): PromptVersionGroup | null {
    const group = this.getVersionGroup(reportData);
    if (!group) return null;

    const version = group.versions.find(v => v.id === versionId);
    if (!version) return null;

    // If trying to modify the original version, create a new version instead
    if (version.isOriginal && version.content !== content) {
      return this.createNewVersion(reportData, content, versionId);
    }

    // Update existing version
    version.content = content;
    version.timestamp = Date.now();

    this.saveVersionGroup(reportData, group);
    return group;
  }

  // Switch to a different version
  static switchToVersion(reportData: ReportViewModel | null | undefined, versionId: string): PromptVersionGroup | null {
    const group = this.getVersionGroup(reportData);
    if (!group) return null;

    const version = group.versions.find(v => v.id === versionId);
    if (!version) return null;

    group.activeVersionId = versionId;
    this.saveVersionGroup(reportData, group);
    return group;
  }

  // Get active version
  static getActiveVersion(reportData: ReportViewModel | null | undefined): PromptVersion | null {
    const group = this.getVersionGroup(reportData);
    if (!group) return null;

    return group.versions.find(v => v.id === group.activeVersionId) || null;
  }

  // Delete a version (except original)
  static deleteVersion(reportData: ReportViewModel | null | undefined, versionId: string): PromptVersionGroup | null {
    const group = this.getVersionGroup(reportData);
    if (!group) return null;

    const version = group.versions.find(v => v.id === versionId);
    if (!version || version.isOriginal) return group; // Can't delete original

    // Remove version
    group.versions = group.versions.filter(v => v.id !== versionId);

    // If we deleted the active version, switch to original
    if (group.activeVersionId === versionId) {
      group.activeVersionId = 'original';
    }

    this.saveVersionGroup(reportData, group);
    return group;
  }

  // Check if content has unsaved changes
  static hasUnsavedChanges(reportData: ReportViewModel | null | undefined, currentContent: string): boolean {
    const activeVersion = this.getActiveVersion(reportData);
    if (!activeVersion) {
      // console.log('No active version found, checking if content is not empty:', currentContent.trim() !== '');
      return currentContent.trim() !== '';
    }

    const hasChanges = activeVersion.content !== currentContent;
    /* console.log('Comparing versions:', { 
      activeContent: activeVersion.content, 
      currentContent, 
      hasChanges,
      activeVersionId: activeVersion.id 
    }); */
    return hasChanges;
  }

  // Clear all prompt versions (global and any legacy entries)
  static clearVersions(_reportData?: ReportViewModel | null | undefined): void {
    try {
      // Remove the global key
      localStorage.removeItem(PROMPT_VERSION_CACHE_KEY);

      // Also remove any legacy keys that might still exist
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('ai-editor-prompt-versions-')) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch {
          // Ignore errors
        }
      });
    } catch (error) {
      console.error('Failed to clear prompt versions:', error);
    }
  }

  // Get cache statistics
  static getCacheStats(): { totalGroups: number; totalVersions: number; totalSize: number } {
    let totalGroups = 0;
    let totalVersions = 0;
    let totalSize = 0;

    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('ai-editor-prompt-versions-')) {
          totalGroups++;
          const data = localStorage.getItem(key);
          if (data) {
            totalSize += data.length;
            try {
              const group: PromptVersionGroup = JSON.parse(data);
              totalVersions += group.versions.length;
            } catch (e) {
              // Skip invalid entries
            }
          }
        }
      });
    } catch (error) {
      console.error('Failed to get prompt version cache stats:', error);
    }

    return { totalGroups, totalVersions, totalSize };
  }
}
